import Phaser from 'phaser';
import type { GhostName } from '@/types/ghost';
import type { MapData } from '@/types/map-data';
import type { MazeGraph } from '@/ai/pathfinding';
import { COLORS } from '@/config/colors';
import { SPEED, GHOST_ROUTING_ACCURACY } from '@/config/speed';
import { tileToPixelX, tileToPixelY, pixelToTileX, pixelToTileY } from '@/maps/map-renderer';

type GhostMode = 'normal' | 'frightened' | 'returning';

type BehaviorMode = 'chase' | 'scatter';

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

const GHOST_RADIUS = 7;
const BASE_MOVE_SPEED = 120;

/** BFS-distance penalty added when another ghost occupies a corridor tile ahead of Clyde. */
const GHOST_CORRIDOR_PENALTY = 8;


const GHOST_COLORS: Record<GhostName, number> = {
  blinky: COLORS.GHOST_BLINKY,
  speedy: COLORS.GHOST_SPEEDY,
  clyde: COLORS.GHOST_CLYDE,
  inky: COLORS.GHOST_INKY,
} as const;

const GHOST_SPEED_MULTIPLIERS: Record<GhostName, number> = {
  blinky: SPEED.GHOST_BLINKY,
  speedy: SPEED.GHOST_SPEEDY,
  clyde: SPEED.GHOST_CLYDE,
  inky: SPEED.GHOST_INKY,
} as const;

interface GhostSpawnOffset {
  readonly dx: number;
  readonly dy: number;
}

// Ghosts line up in a row across the center of the base (row = releaseY + 2)
const GHOST_SPAWN_OFFSETS: Record<GhostName, GhostSpawnOffset> = {
  blinky: { dx: -1, dy: 2 },
  speedy: { dx: 0, dy: 2 },
  clyde: { dx: 1, dy: 2 },
  inky: { dx: 2, dy: 2 },
} as const;

interface DirectionDelta {
  readonly dx: number;
  readonly dy: number;
}

const DIRECTION_DELTAS: Record<Exclude<Direction, 'none'>, DirectionDelta> = {
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
} as const;

const ALL_DIRECTIONS: ReadonlyArray<Exclude<Direction, 'none'>> = ['up', 'down', 'left', 'right'];

function getOppositeDirection(dir: Direction): Direction {
  switch (dir) {
    case 'left': return 'right';
    case 'right': return 'left';
    case 'up': return 'down';
    case 'down': return 'up';
    default: return 'none';
  }
}

export class GhostEntity {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private debugGraphics: Phaser.GameObjects.Graphics | null = null;
  private readonly name: GhostName;
  private readonly color: number;
  private readonly mapData: MapData;
  private readonly speedMultiplier: number;
  private readonly globalSpeedMultiplier: number;
  private readonly routingAccuracy: number;
  private readonly mazeGraph: MazeGraph;
  private debugRoutes = false;

  private pixelX: number;
  private pixelY: number;
  private tileX: number;
  private tileY: number;
  private currentDirection: Direction = 'none';
  private moving = false;
  private targetPixelX: number;
  private targetPixelY: number;
  private released = false;
  private releasePhase: 'in_base' | 'exiting' | 'released' | 'entering' = 'in_base';
  private exitTargetX: number;
  private exitTargetY: number;
  private frightened = false;
  private flashing = false;
  private flashTimer = 0;
  private returning = false;
  private baseTileX: number;
  private baseTileY: number;
  private pacmanTileX = 0;
  private pacmanTileY = 0;
  private pacmanDirection: Direction = 'none';
  private otherGhostPositions: ReadonlyArray<{ readonly x: number; readonly y: number }> = [];
  private behaviorMode: BehaviorMode = 'scatter';
  constructor(
    scene: Phaser.Scene,
    name: GhostName,
    baseTileX: number,
    baseTileY: number,
    mapData: MapData,
    mazeGraph: MazeGraph,
    globalSpeedMultiplier = 1,
  ) {
    this.name = name;
    this.color = GHOST_COLORS[name];
    this.mapData = mapData;
    this.speedMultiplier = GHOST_SPEED_MULTIPLIERS[name];
    this.globalSpeedMultiplier = globalSpeedMultiplier;
    this.routingAccuracy = GHOST_ROUTING_ACCURACY[name] ?? 0.5;
    this.mazeGraph = mazeGraph;

    const offset = GHOST_SPAWN_OFFSETS[name];
    this.tileX = baseTileX + offset.dx;
    this.tileY = baseTileY + offset.dy;
    this.pixelX = tileToPixelX(this.tileX);
    this.pixelY = tileToPixelY(this.tileY);
    this.targetPixelX = this.pixelX;
    this.targetPixelY = this.pixelY;

    // Store base position for returning
    this.baseTileX = baseTileX;
    this.baseTileY = baseTileY;

    // Exit target is above the ghost base
    this.exitTargetX = mapData.ghostReleasePoint.x;
    this.exitTargetY = mapData.ghostReleasePoint.y - 1;

    this.graphics = scene.add.graphics();

    // Debug route rendering (enable via ?routes=1 query param or hash #routes)
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    if (params.get('routes') === '1' || hash.includes('routes')) {
      this.debugRoutes = true;
      this.debugGraphics = scene.add.graphics();
      this.debugGraphics.setDepth(200);
    }

    this.draw();
  }

  getName(): GhostName {
    return this.name;
  }

  getColor(): number {
    return this.color;
  }

  getTileX(): number {
    return this.tileX;
  }

  getTileY(): number {
    return this.tileY;
  }

  getPixelX(): number {
    return this.pixelX;
  }

  getPixelY(): number {
    return this.pixelY;
  }

  isReleased(): boolean {
    return this.released;
  }

  isFrightened(): boolean {
    return this.frightened;
  }

  isReturning(): boolean {
    return this.returning;
  }

  getMode(): GhostMode {
    if (this.returning) return 'returning';
    if (this.frightened) return 'frightened';
    return 'normal';
  }

  setPacManPosition(tileX: number, tileY: number): void {
    this.pacmanTileX = tileX;
    this.pacmanTileY = tileY;
  }

  setPacManDirection(direction: Direction): void {
    this.pacmanDirection = direction;
  }

  setOtherGhostPositions(positions: ReadonlyArray<{ readonly x: number; readonly y: number }>): void {
    this.otherGhostPositions = positions;
  }

  setBehaviorMode(mode: BehaviorMode): void {
    const wasScatter = this.behaviorMode === 'scatter';
    const wasChaseToBecomeScatter = this.behaviorMode === 'chase' && mode === 'scatter';

    this.behaviorMode = mode;

    // Reverse direction when switching between chase and scatter (classic Pac-Man behavior)
    // but only if released and not frightened/returning
    if (
      this.released &&
      !this.frightened &&
      !this.returning &&
      this.currentDirection !== 'none' &&
      (wasScatter !== (mode === 'scatter') || wasChaseToBecomeScatter)
    ) {
      this.currentDirection = getOppositeDirection(this.currentDirection) as Exclude<Direction, 'none'>;
      if (!this.moving) {
        this.chooseNextDirection();
      }
    }
  }

  getBehaviorMode(): BehaviorMode {
    return this.behaviorMode;
  }

  setReturning(): void {
    this.returning = true;
    this.frightened = false;
    this.flashing = false;
    this.flashTimer = 0;

    // Snap to nearest tile center for clean tile-based pathfinding back to base
    this.tileX = pixelToTileX(this.pixelX);
    this.tileY = pixelToTileY(this.pixelY);
    this.pixelX = tileToPixelX(this.tileX);
    this.pixelY = tileToPixelY(this.tileY);
    this.targetPixelX = this.pixelX;
    this.targetPixelY = this.pixelY;
    this.moving = false;
  }

  setFrightened(value: boolean): void {
    // Don't set frightened on returning ghosts
    if (this.returning) {
      return;
    }

    const wasNormal = !this.frightened && value;
    this.frightened = value;

    // Clear flashing when exiting frightened mode
    if (!value) {
      this.flashing = false;
      this.flashTimer = 0;
    }

    // When entering frightened mode, reverse direction
    if (wasNormal && this.released && this.currentDirection !== 'none') {
      this.currentDirection = getOppositeDirection(this.currentDirection) as Exclude<Direction, 'none'>;
      // Recalculate target based on reversed direction
      if (!this.moving) {
        this.chooseNextDirection();
      }
    }
  }

  setFlashing(value: boolean): void {
    this.flashing = value;
    if (!value) {
      this.flashTimer = 0;
    }
  }

  isFlashing(): boolean {
    return this.flashing;
  }

  release(): void {
    if (this.releasePhase === 'in_base') {
      this.releasePhase = 'exiting';
    }
  }

  resetToBase(baseTileX: number, baseTileY: number): void {
    const offset = GHOST_SPAWN_OFFSETS[this.name];
    this.tileX = baseTileX + offset.dx;
    this.tileY = baseTileY + offset.dy;
    this.pixelX = tileToPixelX(this.tileX);
    this.pixelY = tileToPixelY(this.tileY);
    this.targetPixelX = this.pixelX;
    this.targetPixelY = this.pixelY;
    this.currentDirection = 'none';
    this.moving = false;
    this.released = false;
    this.releasePhase = 'in_base';
    this.returning = false;
    this.frightened = false;
    this.flashing = false;
    this.flashTimer = 0;
    // Don't reset behaviorMode - it's managed by game-scene timer
    this.draw();
  }

  update(_time: number, delta: number): void {
    // Update flash timer for warning animation
    if (this.flashing) {
      this.flashTimer += delta;
    }

    if (this.releasePhase === 'in_base') {
      this.draw();
      return;
    }

    if (this.releasePhase === 'exiting') {
      this.handleExitingBase(delta);
      this.draw();
      return;
    }

    if (this.releasePhase === 'entering') {
      this.handleEnteringBase(delta);
      this.draw();
      return;
    }

    // Handle returning to base (eyes only mode)
    if (this.returning) {
      this.handleReturningToBase(delta);
      this.draw();
      return;
    }

    // Normal maze movement
    if (!this.moving) {
      this.chooseNextDirection();
    }

    if (this.moving) {
      this.moveTowardTarget(delta);
    }

    this.draw();
  }

  private handleExitingBase(delta: number): void {
    const exitPixelX = tileToPixelX(this.exitTargetX);
    const exitPixelY = tileToPixelY(this.exitTargetY);

    const speed = BASE_MOVE_SPEED * this.speedMultiplier * this.mapData.speedMultiplier * this.globalSpeedMultiplier * (delta / 1000);
    const dx = exitPixelX - this.pixelX;
    const dy = exitPixelY - this.pixelY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= speed) {
      this.pixelX = exitPixelX;
      this.pixelY = exitPixelY;
      this.tileX = this.exitTargetX;
      this.tileY = this.exitTargetY;
      this.targetPixelX = this.pixelX;
      this.targetPixelY = this.pixelY;
      this.releasePhase = 'released';
      this.released = true;
      this.currentDirection = 'none';
      this.moving = false;
    } else {
      this.pixelX += (dx / dist) * speed;
      this.pixelY += (dy / dist) * speed;
    }
  }

  private handleReturningToBase(delta: number): void {
    // Navigate through maze routes tile-by-tile toward the exit point above the base
    if (!this.moving) {
      // Check if we've reached the exit target
      if (this.tileX === this.exitTargetX && this.tileY === this.exitTargetY) {
        // Reached the base entrance - smoothly enter and move to home position
        this.returning = false;
        this.currentDirection = 'none';
        this.moving = false;
        this.releasePhase = 'entering';
        return;
      }

      // Always take the optimal route back to base using BFS pathfinding
      this.chooseReturningDirection();
    }

    if (this.moving) {
      this.moveTowardTarget(delta);
    }
  }

  /** Smoothly move from exit point down to home position inside the base. */
  private handleEnteringBase(delta: number): void {
    const offset = GHOST_SPAWN_OFFSETS[this.name];
    const homePixelX = tileToPixelX(this.baseTileX + offset.dx);
    const homePixelY = tileToPixelY(this.baseTileY + offset.dy);

    const speed = BASE_MOVE_SPEED * this.speedMultiplier * this.mapData.speedMultiplier * this.globalSpeedMultiplier * (delta / 1000);
    const dx = homePixelX - this.pixelX;
    const dy = homePixelY - this.pixelY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= speed) {
      // Arrived at home position
      this.tileX = this.baseTileX + offset.dx;
      this.tileY = this.baseTileY + offset.dy;
      this.pixelX = homePixelX;
      this.pixelY = homePixelY;
      this.targetPixelX = this.pixelX;
      this.targetPixelY = this.pixelY;
      this.released = false;
      this.releasePhase = 'in_base';
      // Immediately start exiting again
      this.releasePhase = 'exiting';
    } else {
      this.pixelX += (dx / dist) * speed;
      this.pixelY += (dy / dist) * speed;
    }
  }

  /**
   * Choose the best maze direction to reach the base exit point.
   * Always uses BFS-optimal pathfinding (no randomness for returning).
   */
  private chooseReturningDirection(): void {
    const availableDirections = this.getAvailableDirections();
    if (availableDirections.length === 0) {
      return;
    }

    // At intersections, prefer not to reverse direction (unless it's the only option)
    const opposite = getOppositeDirection(this.currentDirection);
    const preferred = availableDirections.filter((d) => d !== opposite);
    const choices = preferred.length > 0 ? preferred : availableDirections;

    // Use BFS distance map to pick the direction closest to base exit
    const chosen = this.chooseOptimalDirectionByDistance(
      choices,
      (tileX, tileY) => this.mazeGraph.getDistanceToBase(tileX, tileY),
    );
    if (!chosen) {
      return;
    }

    this.applyDirectionChoice(chosen);
  }

  private chooseNextDirection(): void {
    const availableDirections = this.getAvailableDirections();

    if (availableDirections.length === 0) {
      return;
    }

    // At intersections, prefer not to reverse direction
    const opposite = getOppositeDirection(this.currentDirection);
    const preferred = availableDirections.filter((d) => d !== opposite);
    const choices = preferred.length > 0 ? preferred : availableDirections;

    // Frightened ghosts keep their personality but move slower
    const chosen = this.chooseByPersonality(choices);

    if (!chosen) {
      return;
    }

    this.applyDirectionChoice(chosen);
  }

  /**
   * Apply a direction choice: set direction, compute next tile, handle tunnels, start moving.
   */
  private applyDirectionChoice(chosen: Exclude<Direction, 'none'>): void {
    this.currentDirection = chosen;
    const deltas = DIRECTION_DELTAS[chosen];
    let nextTileX = this.tileX + deltas.dx;
    const nextTileY = this.tileY + deltas.dy;

    // Handle tunnel wrapping
    if (nextTileX < 0) {
      nextTileX = this.mapData.width - 1;
      this.tileX = nextTileX;
      this.pixelX = tileToPixelX(nextTileX);
      this.targetPixelX = this.pixelX;
      this.targetPixelY = this.pixelY;
      return;
    }
    if (nextTileX >= this.mapData.width) {
      nextTileX = 0;
      this.tileX = nextTileX;
      this.pixelX = tileToPixelX(nextTileX);
      this.targetPixelX = this.pixelX;
      this.targetPixelY = this.pixelY;
      return;
    }

    this.tileX = nextTileX;
    this.tileY = nextTileY;
    this.targetPixelX = tileToPixelX(nextTileX);
    this.targetPixelY = tileToPixelY(nextTileY);
    this.moving = true;
  }

  /**
   * Ghost AI: probability-based optimal routing.
   * In scatter mode: all ghosts target their scatter corner with full accuracy.
   * In chase mode: each ghost has a probability of taking the BFS-optimal
   * route toward Pac-Man; otherwise picks a random direction.
   * Frightened ghosts keep the same routing personality but FLEE from
   * Pac-Man (maximize distance) at 50% speed instead of chasing.
   */
  private chooseByPersonality(
    choices: Array<Exclude<Direction, 'none'>>,
  ): Exclude<Direction, 'none'> | undefined {
    // Frightened ghosts: same personality accuracy but flee instead of chase
    if (this.frightened) {
      if (Math.random() < this.routingAccuracy) {
        // Use BFS distance to pick the direction that moves AWAY from Pac-Man
        return this.chooseWorstDirectionByDistance(
          choices,
          (tileX, tileY) => this.mazeGraph.getDistanceToTarget(
            tileX, tileY, this.pacmanTileX, this.pacmanTileY,
          ),
        );
      }
      // Random direction
      return choices[Math.floor(Math.random() * choices.length)];
    }

    // In scatter mode, all ghosts target their scatter corner
    if (this.behaviorMode === 'scatter') {
      return this.chooseOptimalDirectionByDistance(
        choices,
        (tileX, tileY) => this.mazeGraph.getScatterDistance(this.name, tileX, tileY),
      );
    }

    // Chase mode: each ghost has a probability of picking optimal route
    if (Math.random() < this.routingAccuracy) {
      // Blinky (red): target the next junction Pac-Man will reach
      if (this.name === 'blinky') {
        return this.chooseBlinkyDirection(choices);
      }

      // Clyde (yellow): best route but penalize corridors occupied by other ghosts
      if (this.name === 'clyde') {
        return this.chooseClydeDirection(choices);
      }

      return this.chooseOptimalDirectionByDistance(
        choices,
        (tileX, tileY) => this.mazeGraph.getDistanceToTarget(
          tileX, tileY, this.pacmanTileX, this.pacmanTileY,
        ),
      );
    }

    // Random direction
    return choices[Math.floor(Math.random() * choices.length)];
  }

  /**
   * Pick the direction from choices that leads to the tile with
   * the GREATEST BFS distance (flee from target). Used by frightened ghosts.
   */
  private chooseWorstDirectionByDistance(
    choices: Array<Exclude<Direction, 'none'>>,
    getDistance: (tileX: number, tileY: number) => number,
  ): Exclude<Direction, 'none'> | undefined {
    let bestDir: Exclude<Direction, 'none'> | undefined;
    let bestDist = -1;

    for (const dir of choices) {
      const delta = DIRECTION_DELTAS[dir];
      let nextX = this.tileX + delta.dx;
      const nextY = this.tileY + delta.dy;

      // Handle tunnel wrapping for distance evaluation
      if (nextX < 0) nextX = this.mapData.width - 1;
      if (nextX >= this.mapData.width) nextX = 0;

      const dist = getDistance(nextX, nextY);
      if (dist > bestDist && dist !== Infinity) {
        bestDist = dist;
        bestDir = dir;
      }
    }

    // Fallback to random if all distances are 0 or Infinity
    if (!bestDir) {
      return choices[Math.floor(Math.random() * choices.length)];
    }

    return bestDir;
  }

  /**
   * Pick the direction from choices that leads to the tile with
   * the smallest BFS distance (via the provided distance function).
   */
  private chooseOptimalDirectionByDistance(
    choices: Array<Exclude<Direction, 'none'>>,
    getDistance: (tileX: number, tileY: number) => number,
  ): Exclude<Direction, 'none'> | undefined {
    let bestDir: Exclude<Direction, 'none'> | undefined;
    let bestDist = Infinity;

    for (const dir of choices) {
      const delta = DIRECTION_DELTAS[dir];
      let nextX = this.tileX + delta.dx;
      const nextY = this.tileY + delta.dy;

      // Handle tunnel wrapping for distance evaluation
      if (nextX < 0) nextX = this.mapData.width - 1;
      if (nextX >= this.mapData.width) nextX = 0;

      const dist = getDistance(nextX, nextY);
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = dir;
      }
    }

    // Fallback to Manhattan distance if BFS gives no result (all Infinity)
    if (bestDist === Infinity) {
      return this.chooseDirectionByManhattan(choices, this.pacmanTileX, this.pacmanTileY);
    }

    return bestDir;
  }

  /**
   * Fallback: Manhattan distance heuristic for cases where BFS has no path.
   */
  private chooseDirectionByManhattan(
    choices: Array<Exclude<Direction, 'none'>>,
    targetTileX: number,
    targetTileY: number,
  ): Exclude<Direction, 'none'> | undefined {
    let bestDir: Exclude<Direction, 'none'> | undefined;
    let bestDist = Infinity;

    for (const dir of choices) {
      const delta = DIRECTION_DELTAS[dir];
      const nextX = this.tileX + delta.dx;
      const nextY = this.tileY + delta.dy;
      const dist = Math.abs(nextX - targetTileX) + Math.abs(nextY - targetTileY);
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = dir;
      }
    }

    return bestDir;
  }

  /**
   * Blinky (red): instead of targeting Pac-Man directly, assess Pac-Man's
   * direction of travel and aim at the next junction Pac-Man will reach.
   * Falls back to Pac-Man's current tile when direction is unknown or
   * no junction lies ahead.
   */
  private chooseBlinkyDirection(
    choices: Array<Exclude<Direction, 'none'>>,
  ): Exclude<Direction, 'none'> | undefined {
    let targetX = this.pacmanTileX;
    let targetY = this.pacmanTileY;

    if (this.pacmanDirection !== 'none') {
      const junction = this.mazeGraph.findNextJunction(
        this.pacmanTileX, this.pacmanTileY, this.pacmanDirection as Exclude<Direction, 'none'>,
      );
      if (junction) {
        targetX = junction.x;
        targetY = junction.y;
      }
    }

    return this.chooseOptimalDirectionByDistance(
      choices,
      (tileX, tileY) => this.mazeGraph.getDistanceToTarget(tileX, tileY, targetX, targetY),
    );
  }

  /**
   * Clyde (yellow): use the best BFS route toward Pac-Man, but add a distance
   * penalty for each direction whose corridor (up to the next junction)
   * already contains another ghost. This encourages Clyde to flank from a
   * different angle rather than stacking behind another ghost.
   */
  private chooseClydeDirection(
    choices: Array<Exclude<Direction, 'none'>>,
  ): Exclude<Direction, 'none'> | undefined {
    let bestDir: Exclude<Direction, 'none'> | undefined;
    let bestScore = Infinity;

    for (const dir of choices) {
      const delta = DIRECTION_DELTAS[dir];
      let nextX = this.tileX + delta.dx;
      const nextY = this.tileY + delta.dy;

      // Handle tunnel wrapping
      if (nextX < 0) nextX = this.mapData.width - 1;
      if (nextX >= this.mapData.width) nextX = 0;

      // Base BFS distance to Pac-Man from the candidate tile
      const dist = this.mazeGraph.getDistanceToTarget(
        nextX, nextY, this.pacmanTileX, this.pacmanTileY,
      );

      // Walk the corridor from current tile in this direction and check
      // whether another ghost sits on any tile before the next junction
      const corridorTiles = this.mazeGraph.getCorridorTiles(this.tileX, this.tileY, dir);
      let penalty = 0;
      for (const tile of corridorTiles) {
        for (const gp of this.otherGhostPositions) {
          if (gp.x === tile.x && gp.y === tile.y) {
            penalty += GHOST_CORRIDOR_PENALTY;
          }
        }
      }

      const score = dist + penalty;
      if (score < bestScore) {
        bestScore = score;
        bestDir = dir;
      }
    }

    if (bestScore === Infinity) {
      return this.chooseDirectionByManhattan(choices, this.pacmanTileX, this.pacmanTileY);
    }

    return bestDir;
  }

  private getAvailableDirections(): Array<Exclude<Direction, 'none'>> {
    const available: Array<Exclude<Direction, 'none'>> = [];

    for (const dir of ALL_DIRECTIONS) {
      const delta = DIRECTION_DELTAS[dir];
      const nextX = this.tileX + delta.dx;
      const nextY = this.tileY + delta.dy;

      // Handle tunnel wrapping
      if (nextX < 0 || nextX >= this.mapData.width) {
        if (this.isTunnelRow(this.tileY)) {
          available.push(dir);
        }
        continue;
      }

      if (this.isGhostWalkable(nextX, nextY)) {
        available.push(dir);
      }
    }

    return available;
  }

  private isGhostWalkable(tileX: number, tileY: number): boolean {
    if (tileX < 0 || tileX >= this.mapData.width || tileY < 0 || tileY >= this.mapData.height) {
      return false;
    }
    const row = this.mapData.tiles[tileY];
    if (!row) {
      return false;
    }
    const tile = row[tileX];
    if (tile === 'wall') {
      return false;
    }
    // Released ghosts cannot re-enter the ghost base (one-way door)
    // Only exiting and returning ghosts can walk on ghost_base tiles
    if (tile === 'ghost_base' && this.released && !this.returning) {
      return false;
    }
    return true;
  }

  private isTunnelRow(tileY: number): boolean {
    return this.mapData.tunnelPairs.some(
      (pair) => pair.entry.y === tileY || pair.exit.y === tileY,
    );
  }

  private moveTowardTarget(delta: number): void {
    let effectiveMultiplier = this.speedMultiplier;
    if (this.returning) {
      effectiveMultiplier = SPEED.GHOST_RETURNING;
    } else if (this.frightened) {
      // Frightened speed = 50% of individual ghost's normal speed
      effectiveMultiplier = this.speedMultiplier * SPEED.GHOST_FRIGHTENED_FACTOR;
    }
    const speed = BASE_MOVE_SPEED * effectiveMultiplier * this.mapData.speedMultiplier * this.globalSpeedMultiplier * (delta / 1000);
    const dx = this.targetPixelX - this.pixelX;
    const dy = this.targetPixelY - this.pixelY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= speed) {
      this.pixelX = this.targetPixelX;
      this.pixelY = this.targetPixelY;
      this.moving = false;
    } else {
      this.pixelX += (dx / dist) * speed;
      this.pixelY += (dy / dist) * speed;
    }
  }

  private draw(): void {
    this.graphics.clear();

    // Debug route overlay
    if (this.debugRoutes) {
      this.drawDebugRoute();
    }

    // When returning or entering base, only draw eyes (no body)
    if (this.returning || this.releasePhase === 'entering') {
      this.drawEyes();
      return;
    }

    const isFlashWhite = this.flashing && this.isFlashWhiteFrame();
    const bodyColor = this.frightened
      ? (isFlashWhite ? 0xffffff : COLORS.GHOST_FRIGHTENED)
      : this.color;

    // Neon glow behind the ghost
    this.graphics.fillStyle(bodyColor, 0.12);
    this.graphics.fillCircle(this.pixelX, this.pixelY, GHOST_RADIUS + 5);
    this.graphics.fillStyle(bodyColor, 0.06);
    this.graphics.fillCircle(this.pixelX, this.pixelY, GHOST_RADIUS + 8);

    // Ghost body (rounded top, flat bottom with wavy skirt)
    this.graphics.fillStyle(bodyColor, 1);

    // Draw rounded top (semicircle)
    this.graphics.beginPath();
    this.graphics.arc(
      this.pixelX,
      this.pixelY - 1,
      GHOST_RADIUS,
      Math.PI,
      0,
    );

    // Draw body sides
    this.graphics.lineTo(this.pixelX + GHOST_RADIUS, this.pixelY + GHOST_RADIUS - 2);

    // Draw wavy bottom
    const waveWidth = GHOST_RADIUS * 2 / 3;
    this.graphics.lineTo(this.pixelX + GHOST_RADIUS - waveWidth * 0.5, this.pixelY + GHOST_RADIUS);
    this.graphics.lineTo(this.pixelX + GHOST_RADIUS - waveWidth, this.pixelY + GHOST_RADIUS - 2);
    this.graphics.lineTo(this.pixelX, this.pixelY + GHOST_RADIUS);
    this.graphics.lineTo(this.pixelX - GHOST_RADIUS + waveWidth, this.pixelY + GHOST_RADIUS - 2);
    this.graphics.lineTo(this.pixelX - GHOST_RADIUS + waveWidth * 0.5, this.pixelY + GHOST_RADIUS);
    this.graphics.lineTo(this.pixelX - GHOST_RADIUS, this.pixelY + GHOST_RADIUS - 2);

    this.graphics.closePath();
    this.graphics.fillPath();

    // Draw eyes (different style when frightened/flashing)
    if (this.frightened) {
      this.drawFrightenedFace(isFlashWhite);
    } else {
      this.drawEyes();
    }
  }

  private drawFrightenedFace(isFlashWhite: boolean): void {
    // Frightened ghosts have small dot eyes and a wavy mouth
    // When flashing white, features are drawn in red for contrast
    const featureColor = isFlashWhite ? 0xff0000 : 0xffffff;
    const eyeOffsetX = 3;
    const eyeOffsetY = -2;

    // Small dot eyes
    this.graphics.fillStyle(featureColor, 1);
    this.graphics.fillCircle(this.pixelX - eyeOffsetX, this.pixelY + eyeOffsetY, 1.2);
    this.graphics.fillCircle(this.pixelX + eyeOffsetX, this.pixelY + eyeOffsetY, 1.2);

    // Wavy frown mouth
    this.graphics.lineStyle(1, featureColor, 1);
    this.graphics.beginPath();
    const mouthY = this.pixelY + 3;
    this.graphics.moveTo(this.pixelX - 4, mouthY);
    this.graphics.lineTo(this.pixelX - 2, mouthY - 1.5);
    this.graphics.lineTo(this.pixelX, mouthY);
    this.graphics.lineTo(this.pixelX + 2, mouthY - 1.5);
    this.graphics.lineTo(this.pixelX + 4, mouthY);
    this.graphics.strokePath();
  }

  private isFlashWhiteFrame(): boolean {
    // Flash every 200ms: alternating between blue and white
    const flashCycleMs = 200;
    return Math.floor(this.flashTimer / flashCycleMs) % 2 === 1;
  }

  private drawEyes(): void {
    const eyeOffsetX = 3;
    const eyeOffsetY = -2;
    const eyeRadius = 2.5;
    const pupilRadius = 1.2;

    // Pupil direction offset based on movement
    let pupilDirX = 0;
    let pupilDirY = 0;
    if (this.currentDirection === 'left') { pupilDirX = -0.8; }
    if (this.currentDirection === 'right') { pupilDirX = 0.8; }
    if (this.currentDirection === 'up') { pupilDirY = -0.8; }
    if (this.currentDirection === 'down') { pupilDirY = 0.8; }

    // Left eye white
    this.graphics.fillStyle(0xffffff, 1);
    this.graphics.fillCircle(
      this.pixelX - eyeOffsetX,
      this.pixelY + eyeOffsetY,
      eyeRadius,
    );

    // Right eye white
    this.graphics.fillCircle(
      this.pixelX + eyeOffsetX,
      this.pixelY + eyeOffsetY,
      eyeRadius,
    );

    // Left pupil
    this.graphics.fillStyle(0x110044, 1);
    this.graphics.fillCircle(
      this.pixelX - eyeOffsetX + pupilDirX,
      this.pixelY + eyeOffsetY + pupilDirY,
      pupilRadius,
    );

    // Right pupil
    this.graphics.fillCircle(
      this.pixelX + eyeOffsetX + pupilDirX,
      this.pixelY + eyeOffsetY + pupilDirY,
      pupilRadius,
    );
  }

  private drawDebugRoute(): void {
    if (!this.debugGraphics) return;
    this.debugGraphics.clear();

    if (this.releasePhase !== 'released' && !this.returning) return;

    // Get path based on current ghost mode
    let path: Array<{ x: number; y: number }>;
    const offset = GHOST_SPAWN_OFFSETS[this.name];
    const homeX = this.baseTileX + offset.dx;
    const homeY = this.baseTileY + offset.dy;

    if (this.returning) {
      path = this.mazeGraph.getPathToBase(this.tileX, this.tileY);
      // Append in-base leg: exit point -> home position
      path.push({ x: homeX, y: homeY });
    } else if (this.releasePhase === 'entering') {
      // Currently sliding into base — show line from current pixel to home
      path = [{ x: this.tileX, y: this.tileY }, { x: homeX, y: homeY }];
    } else if (this.frightened) {
      return;
    } else if (this.behaviorMode === 'scatter') {
      path = this.mazeGraph.getPathToScatter(this.name, this.tileX, this.tileY);
    } else if (this.name === 'blinky' && this.pacmanDirection !== 'none') {
      // Blinky debug route: show path to the junction Pac-Man is heading toward
      const junction = this.mazeGraph.findNextJunction(
        this.pacmanTileX, this.pacmanTileY, this.pacmanDirection as Exclude<Direction, 'none'>,
      );
      if (junction) {
        path = this.mazeGraph.getPathToTarget(this.tileX, this.tileY, junction.x, junction.y);
      } else {
        path = this.mazeGraph.getPathToTarget(this.tileX, this.tileY, this.pacmanTileX, this.pacmanTileY);
      }
    } else {
      path = this.mazeGraph.getPathToTarget(this.tileX, this.tileY, this.pacmanTileX, this.pacmanTileY);
    }

    if (!path || path.length < 2) return;

    // Draw path lines in ghost's color
    this.debugGraphics.lineStyle(1.5, this.color, 0.6);
    this.debugGraphics.beginPath();
    this.debugGraphics.moveTo(
      tileToPixelX(path[0]!.x),
      tileToPixelY(path[0]!.y),
    );
    for (let i = 1; i < path.length; i++) {
      this.debugGraphics.lineTo(
        tileToPixelX(path[i]!.x),
        tileToPixelY(path[i]!.y),
      );
    }
    this.debugGraphics.strokePath();

    // Draw destination dot
    const dest = path[path.length - 1]!;
    this.debugGraphics.fillStyle(this.color, 0.9);
    this.debugGraphics.fillCircle(tileToPixelX(dest.x), tileToPixelY(dest.y), 3);
  }

  setVisible(visible: boolean): void {
    this.graphics.setVisible(visible);
    if (this.debugGraphics) {
      this.debugGraphics.setVisible(visible);
    }
  }

  destroy(): void {
    this.graphics.destroy();
    if (this.debugGraphics) {
      this.debugGraphics.destroy();
    }
  }
}
